import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { login as apiLogin } from '../services/api';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiLogin(formData);
      login(response.data.user, response.data.token);
      navigate('/');
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Login - Pelican Beaks</title>
        <meta name="description" content="Log in to your Pelican Beaks account to access your AI travel itineraries and dashboard." />
      </Helmet>
      
      {/* Copied styles from HomePage.js */}
      <style>
        {`
          @keyframes float {
            0% {
              transform: translate(0, 0) scale(1);
            }
            50% {
              transform: translate(2%, 2%) scale(1.02);
            }
            100% {
              transform: translate(-2%, -2%) scale(0.98);
            }
          }

          .background-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
            overflow: hidden;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }

          .gradient-blob {
            position: absolute;
            border-radius: 100%;
            filter: blur(60px);
            opacity: 0.6;
            animation: float 20s infinite alternate ease-in-out;
          }

          .gradient-blob:nth-child(1) {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            width: 150vw;
            height: 150vw;
            top: -100%;
            left: -25%;
            animation-delay: 0s;
          }

          .gradient-blob:nth-child(2) {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
            width: 150vw;
            height: 150vw;
            bottom: -100%;
            right: -25%;
            animation-delay: -5s;
          }

          .gradient-blob:nth-child(3) {
            background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
            width: 100vw;
            height: 100vw;
            top: 30%;
            left: -50%;
            animation-delay: -10s;
          }

          @media (min-width: 640px) {
            .gradient-blob {
              filter: blur(50px);
              opacity: 0.35;
            }
          }

          @media (min-width: 768px) {
            .gradient-blob {
              filter: blur(60px);
              opacity: 0.4;
            }

            .gradient-blob:nth-child(1) {
              width: 120vw;
              height: 120vw;
              top: -70%;
              left: -20%;
            }

            .gradient-blob:nth-child(2) {
              width: 120vw;
              height: 120vw;
              bottom: -70%;
              right: -20%;
            }

            .gradient-blob:nth-child(3) {
              width: 80vw;
              height: 80vw;
              top: 30%;
              left: -30%;
            }
          }

          @media (min-width: 1024px) {
            .gradient-blob {
              filter: blur(80px);
              opacity: 0.5;
            }

            .gradient-blob:nth-child(1) {
              width: 60vw;
              height: 60vw;
              top: -20%;
              left: -10%;
            }

            .gradient-blob:nth-child(2) {
              width: 60vw;
              height: 60vw;
              bottom: -30%;
              right: -10%;
            }

            .gradient-blob:nth-child(3) {
              width: 40vw;
              height: 40vw;
              top: 40%;
              left: 30%;
            }
          }
        `}
      </style>

      {/* Animated Gradient Background */}
      <div className="background-container">
        <div className="gradient-blob"></div>
        <div className="gradient-blob"></div>
        <div className="gradient-blob"></div>
      </div>

      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(12px)',
              // Add a border radius to the paper
              borderRadius: 16
            }}
          >
            <Typography component="h1" variant="h5">
              Sign in
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                {error}
              </Alert>
            )}
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Link 
                  component={RouterLink} 
                  to="/forgot-password" 
                  variant="body2" 
                  sx={{ textDecoration: 'none', fontWeight: 500 }}
                >
                  Forgot Password?
                </Link>
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Link component={RouterLink} to="/register" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/" variant="body2">
                  {"Back to Home Page"}
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}

export default Login;